/**
 * FR-131 · Bung một dòng nhật ký để xem JSON trước/sau, user-agent và request id.
 * Dựng thành một `<tr>` phụ ngay dưới dòng được chọn để không phá cấu trúc bảng.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { useFormat } from '../../lib/format';
import { ErrorState, Skeleton } from '../../components/States';
import type { AuditLogDetail, AuditLogItem } from './types';

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const t = useT();
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="eyebrow">{label}</span>
      {value === null || value === undefined ? (
        <span className="small muted">{t('common.none')}</span>
      ) : (
        <pre className="json-box mono">{JSON.stringify(value, null, 2)}</pre>
      )}
    </div>
  );
}

export function AuditRowDetail({ row, colSpan }: { row: AuditLogItem; colSpan: number }) {
  const { formatDateTime } = useFormat();
  const t = useT();
  const detailQuery = useQuery({
    queryKey: ['audit-logs', row.id],
    queryFn: () => api.get<AuditLogDetail>(`/audit-logs/${row.id}`),
  });

  return (
    <tr data-od-id="audit-row-detail">
      <td colSpan={colSpan} style={{ background: 'var(--surface-2)' }}>
        {detailQuery.isLoading ? (
          <Skeleton height={64} />
        ) : detailQuery.error ? (
          <ErrorState error={detailQuery.error} onRetry={() => void detailQuery.refetch()} />
        ) : detailQuery.data ? (
          <div className="stack">
            <dl className="kv">
              <dt>{t('audit.detail.at')}</dt>
              <dd className="num">{formatDateTime(detailQuery.data.createdAt)}</dd>
              <dt>{t('audit.detail.target')}</dt>
              <dd className="mono small">
                {detailQuery.data.targetType ?? '—'}
                {detailQuery.data.targetId ? ` · ${detailQuery.data.targetId}` : ''}
              </dd>
              <dt>{t('audit.detail.userAgent')}</dt>
              <dd className="small">{detailQuery.data.userAgent ?? t('common.unknown')}</dd>
              <dt>{t('audit.detail.requestId')}</dt>
              <dd className="mono small">{detailQuery.data.requestId ?? '—'}</dd>
            </dl>
            <div className="grid g-2">
              <JsonBlock label={t('audit.detail.before')} value={detailQuery.data.before} />
              <JsonBlock label={t('audit.detail.after')} value={detailQuery.data.after} />
            </div>
          </div>
        ) : null}
      </td>
    </tr>
  );
}
